import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

// Shares Step 2 Remittance's "discount is settled" signal (isDiscountSettled)
// with sibling features that render outside the Remittance subtree — notably
// the service-alerts observer, which must defer showing a service-alert popup
// until discount validation and the Discount Loading overlay have finished.
//
// isDiscountSettled is false throughout the whole discount-busy window (default
// auto-apply pending -> applying -> revalidation -> rate fetch -> loader min-
// visible hold) and true only once all of that has completed.
//
// It also carries a per-slot counter of *amount input events*. The service-alert
// observer samples the receiving amount from the form store, which is written
// only by the debounced fee-fetch completion callback -- so an amount edited and
// restored inside one debounce window commits the value it started from and is
// invisible to any comparison of that store value. Signals derived from discount
// state cannot stand in for it either: a discount cycle starts on remount and on
// a discount apply, neither of which is a user amount change, and treating those
// as one re-shows service alerts the user already dismissed when they merely
// navigate between steps. The counter is incremented from the fee-fetch callback
// itself, which is reached only from the amount input's onChange handler, so it
// witnesses the event rather than its aftermath.
//
// It lives here, rather than in Remittance, because the provider outlives the
// Step-2 subtree: Remittance unmounts on step navigation and would reset it.
//
// Defaults are safe for consumers rendered without a provider (e.g. the base
// AddTransactionPage): isProvided is false, so consumers keep their pre-context
// behaviour and treat discounts as settled.
const DiscountLoadingContext = createContext({
  isProvided: false,
  isDiscountSettled: true,
  setDiscountSettled: () => {},
  amountInputEventSeq1: 0,
  amountInputEventSeq2: 0,
  notifyAmountInputEvent: () => {},
});

export const DiscountLoadingProvider = ({ children }) => {
  const [isDiscountSettled, setDiscountSettled] = useState(true);
  const [amountInputEventSeq1, setAmountInputEventSeq1] = useState(0);
  const [amountInputEventSeq2, setAmountInputEventSeq2] = useState(0);

  // slot: 1 = receiving amount, 2 = add-another-currency receiving amount.
  const notifyAmountInputEvent = useCallback((slot) => {
    if (slot === 2) {
      setAmountInputEventSeq2((seq) => seq + 1);
      return;
    }
    setAmountInputEventSeq1((seq) => seq + 1);
  }, []);

  const value = useMemo(
    () => ({
      isProvided: true,
      isDiscountSettled,
      setDiscountSettled,
      amountInputEventSeq1,
      amountInputEventSeq2,
      notifyAmountInputEvent,
    }),
    [
      isDiscountSettled,
      amountInputEventSeq1,
      amountInputEventSeq2,
      notifyAmountInputEvent,
    ]
  );

  return (
    <DiscountLoadingContext.Provider value={value}>
      {children}
    </DiscountLoadingContext.Provider>
  );
};

export const useDiscountLoadingState = () => useContext(DiscountLoadingContext);

export default DiscountLoadingContext;
